import React, { useEffect, useMemo, useState } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SortIcon from '@mui/icons-material/Sort';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { API_BASE_URL } from '../config/api';

export default function GanttChart({ role = 'other' }) {
    const isSubContractor = role === 'subContractor';
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('startDate');
    const [zoomLevel, setZoomLevel] = useState('month');
    const [hoveredProject, setHoveredProject] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const containerRef = React.useRef(null);

    const zoomOptions = [
        { value: 'day', label: 'Day', unitWidth: 20 },
        { value: 'week', label: 'Week', unitWidth: 40 },
        { value: 'month', label: 'Month', unitWidth: 60 },
        { value: 'quarter', label: 'Quarter', unitWidth: 45 },
        { value: 'year', label: 'Year', unitWidth: 80 },
    ];

    const sortOptions = [
        { value: 'startDate', label: 'Start Date (Earliest)' },
        { value: 'startDateDesc', label: 'Start Date (Latest)' },
        { value: 'name', label: 'Project Name (A-Z)' },
        { value: 'nameDesc', label: 'Project Name (Z-A)' },
        { value: 'progress', label: 'Progress (Low to High)' },
        { value: 'progressDesc', label: 'Progress (High to Low)' },
    ];

    // Color palette for contractors
    const colorPalette = [
        '#3B82F6', // Blue
        '#10B981', // Green
        '#F59E0B', // Amber
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#EC4899', // Pink
        '#14B8A6', // Teal
        '#F97316', // Orange
        '#6366F1', // Indigo
        '#22C55E', // Emerald
    ];

    const getContractorColorMap = (projectsData, isSubContractor) => {
        const contractors = [...new Set(projectsData.map(p => 
            isSubContractor ? p.user?.company_name : p.assign_to?.company_name
        ))];
        const colorMap = {};
        contractors.forEach((contractor, index) => {
            colorMap[contractor] = colorPalette[index % colorPalette.length];
        });
        return colorMap;
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
    };

    const handleZoomChange = (event) => {
        setZoomLevel(event.target.value);
    };

    const sortProjects = (projectsData) => {
        const sorted = [...projectsData];
        switch (sortBy) {
            case 'startDate':
                return sorted.sort((a, b) => new Date(a.project_start_date) - new Date(b.project_start_date));
            case 'startDateDesc':
                return sorted.sort((a, b) => new Date(b.project_start_date) - new Date(a.project_start_date));
            case 'name':
                return sorted.sort((a, b) => a.project_name.localeCompare(b.project_name));
            case 'nameDesc':
                return sorted.sort((a, b) => b.project_name.localeCompare(a.project_name));
            case 'progress':
                return sorted.sort((a, b) => Number(a.status) - Number(b.status));
            case 'progressDesc':
                return sorted.sort((a, b) => Number(b.status) - Number(a.status));
            default:
                return sorted;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const url = API_BASE_URL;
                const token = localStorage.getItem('authToken');
                const requestOptions = {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                };

                const dateParameters = 'start_date=2022-10-25&end_date=2028-10-26';
                const endpoint = role === 'subContractor' ? 'sub_cont_gantt_chart' : 'gantt_chart';
                const fullUrl = `${url}/${endpoint}?${dateParameters}`;
                const response = await fetch(fullUrl, requestOptions);
                const data = await response.json();
                
                setProjects(data.data || []);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [role]);

    const sortedProjects = useMemo(() => sortProjects(projects), [projects, sortBy]);
    const contractorColorMap = useMemo(() => (
        getContractorColorMap(sortedProjects, isSubContractor)
    ), [sortedProjects, isSubContractor]);

    const contractorNames = useMemo(() => (
        sortedProjects
            .map((project) => (isSubContractor ? project.user?.company_name : project.assign_to?.company_name))
            .filter(Boolean)
    ), [sortedProjects, isSubContractor]);

    const showContractorName = useMemo(() => (
        new Set(contractorNames).size > 1
    ), [contractorNames]);

    const displayLabels = useMemo(() => (
        sortedProjects.map((project) => {
            const contractor = isSubContractor ? project.user?.company_name : project.assign_to?.company_name;
            if (!showContractorName || !contractor) {
                return { primary: project.project_name, secondary: null };
            }
            return { primary: project.project_name, secondary: contractor };
        })
    ), [sortedProjects, isSubContractor, showContractorName]);

    const timelineConfig = useMemo(() => {
        if (sortedProjects.length === 0) {
            return null;
        }

        const zoomConfig = zoomOptions.find((z) => z.value === zoomLevel) || zoomOptions[2];
        const unitWidth = zoomConfig.unitWidth;
        const rowHeight = 48;
        const headerHeight = 64;
        const minDate = new Date(Math.min(...sortedProjects.map((p) => new Date(p.project_start_date).getTime())));
        const maxDate = new Date(Math.max(...sortedProjects.map((p) => new Date(p.project_end_date).getTime())));

        const timelineStart = new Date(minDate.getFullYear(), minDate.getMonth() - 1, 1);
        const timelineEnd = new Date(maxDate.getFullYear(), maxDate.getMonth() + 2, 1);

        // Generate time units based on zoom level
        const units = [];
        const current = new Date(timelineStart);

        if (zoomLevel === 'day') {
            while (current < timelineEnd) {
                units.push({ date: new Date(current), label: current.getDate().toString() });
                current.setDate(current.getDate() + 1);
            }
        } else if (zoomLevel === 'week') {
            // Start from beginning of week
            current.setDate(current.getDate() - current.getDay());
            while (current < timelineEnd) {
                const weekStart = new Date(current);
                units.push({ date: weekStart, label: `W${Math.ceil(weekStart.getDate() / 7)}` });
                current.setDate(current.getDate() + 7);
            }
        } else if (zoomLevel === 'month') {
            while (current < timelineEnd) {
                units.push({ date: new Date(current), label: current.toLocaleDateString('en-US', { month: 'short' }) });
                current.setMonth(current.getMonth() + 1);
            }
        } else if (zoomLevel === 'quarter') {
            // Start from beginning of quarter
            current.setMonth(Math.floor(current.getMonth() / 3) * 3);
            while (current < timelineEnd) {
                const q = Math.floor(current.getMonth() / 3) + 1;
                units.push({ date: new Date(current), label: `Q${q}` });
                current.setMonth(current.getMonth() + 3);
            }
        } else if (zoomLevel === 'year') {
            current.setMonth(0);
            current.setDate(1);
            while (current < timelineEnd) {
                units.push({ date: new Date(current), label: current.getFullYear().toString() });
                current.setFullYear(current.getFullYear() + 1);
            }
        }

        // Generate year segments for header
        const yearSegments = [];
        let activeYear = units[0]?.date.getFullYear();
        let count = 0;
        units.forEach((unit, index) => {
            const year = unit.date.getFullYear();
            if (year !== activeYear) {
                yearSegments.push({ year: activeYear, width: count * unitWidth });
                activeYear = year;
                count = 0;
            }
            count += 1;
            if (index === units.length - 1) {
                yearSegments.push({ year: activeYear, width: count * unitWidth });
            }
        });

        return {
            unitWidth,
            rowHeight,
            headerHeight,
            timelineStart,
            timelineEnd,
            units,
            yearSegments,
            zoomLevel,
            timelineWidth: units.length * unitWidth,
            totalDurationMs: timelineEnd.getTime() - timelineStart.getTime(),
        };
    }, [sortedProjects, zoomLevel]);

    const formatYear = (year) => year.toString();
    const formatDate = (dateValue) => (
        new Date(dateValue).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    );

    const calculateDuration = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays < 30) return `${diffDays} days`;
        if (diffDays < 365) return `${Math.round(diffDays / 30)} months`;
        return `${(diffDays / 365).toFixed(1)} years`;
    };

    return (
        <div className="profile_page">
            <div className="container">
                {/* Header Section */}
                <div className="profile_section">
                    <div className="section_header">
                        <div className="section_header_row">
                            <div>
                                <h3>Project Schedule</h3>
                                <p>{isSubContractor 
                                    ? 'Your awarded projects grouped by general contractor' 
                                    : 'Visual timeline of sub-contractor assignments'}</p>
                            </div>
                            <div className="schedule_controls">
                                <div className="time_unit_selector">
                                    <label htmlFor="zoomLevel">
                                        <ZoomInIcon fontSize="small" />
                                        Zoom
                                    </label>
                                    <select
                                        id="zoomLevel"
                                        value={zoomLevel}
                                        onChange={handleZoomChange}
                                        className="form-control"
                                    >
                                        {zoomOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="time_unit_selector">
                                    <label htmlFor="sortBy">
                                        <SortIcon fontSize="small" />
                                        Sort By
                                    </label>
                                    <select
                                        id="sortBy"
                                        value={sortBy}
                                        onChange={handleSortChange}
                                        className="form-control"
                                    >
                                        {sortOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="section_content">
                        {/* Legend */}
                        <div className="chart_legend" style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>How to read:</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{ 
                                        width: '60px', 
                                        height: '20px', 
                                        backgroundColor: 'rgba(59, 130, 246, 0.25)', 
                                        border: '2px solid rgba(59, 130, 246, 1)',
                                        borderRadius: '4px',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '60%',
                                            backgroundColor: 'rgba(59, 130, 246, 1)',
                                            borderRadius: '2px 0 0 2px'
                                        }}></div>
                                    </div>
                                    <span style={{ fontSize: '12px', color: '#555' }}>
                                        Light = Full Duration | Dark = % Complete
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Gantt Chart Container */}
                        {loading ? (
                            <div className="loading_state">
                                <ThreeDots height="60" width="80" radius="9" color="#B1903F" visible={true} />
                                <p>Loading schedule...</p>
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="empty_state">
                                <TimelineIcon className="empty_icon" />
                                <h4>No Projects Scheduled</h4>
                                <p>Your project timeline will appear here once you have active projects.</p>
                            </div>
                        ) : (
                            <div className="custom_gantt_container" ref={containerRef}>
                                <div className="custom_gantt_inner" style={{ minHeight: `${(timelineConfig?.headerHeight || 64) + sortedProjects.length * (timelineConfig?.rowHeight || 48)}px` }}>
                                    <div className="custom_gantt_sidebar">
                                        <div
                                            className="custom_gantt_header_spacer"
                                            style={{ height: `${timelineConfig?.headerHeight || 64}px` }}
                                        />
                                        {displayLabels.map((label, index) => (
                                            <div key={`${label.primary}-${index}`} className="custom_gantt_label_row">
                                                <div className="label_primary">{label.primary}</div>
                                                {label.secondary && (
                                                    <div className="label_secondary">{label.secondary}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="custom_gantt_timeline_wrapper">
                                    <div
                                        className="custom_gantt_timeline"
                                        style={{ minWidth: `${timelineConfig?.timelineWidth || 600}px` }}
                                    >
                                        <div
                                            className="custom_gantt_header"
                                            style={{ height: `${timelineConfig?.headerHeight || 64}px` }}
                                        >
                                            {zoomLevel !== 'year' && (
                                                <div className="custom_gantt_year_row">
                                                    {timelineConfig?.yearSegments.map((segment) => (
                                                        <div
                                                            key={segment.year}
                                                            className="custom_gantt_year_cell"
                                                            style={{ width: `${segment.width}px` }}
                                                        >
                                                            {formatYear(segment.year)}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="custom_gantt_unit_row">
                                                {timelineConfig?.units.map((unit, index) => (
                                                    <div
                                                        key={`${unit.date.toISOString()}-${index}`}
                                                        className="custom_gantt_unit_cell"
                                                        style={{ width: `${timelineConfig.unitWidth}px` }}
                                                    >
                                                        {unit.label}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div
                                            className="custom_gantt_body"
                                            style={{
                                                height: `${sortedProjects.length * (timelineConfig?.rowHeight || 48)}px`,
                                                backgroundSize: `${timelineConfig?.unitWidth || 60}px 100%`,
                                            }}
                                        >
                                            {sortedProjects.map((project, index) => {
                                                const contractor = isSubContractor
                                                    ? project.user?.company_name
                                                    : project.assign_to?.company_name;
                                                const barColor = contractorColorMap[contractor] || colorPalette[0];
                                                const barBackground = `${barColor}33`;
                                                const startMs = new Date(project.project_start_date).getTime();
                                                const endMs = new Date(project.project_end_date).getTime();
                                                const totalDurationMs = timelineConfig?.totalDurationMs || 1;
                                                const timelineStartMs = timelineConfig?.timelineStart.getTime() || 0;
                                                const timelineWidth = timelineConfig?.timelineWidth || 1;
                                                const left = Math.max(
                                                    0,
                                                    ((startMs - timelineStartMs) / totalDurationMs) * timelineWidth
                                                );
                                                const rawWidth = Math.max(
                                                    6,
                                                    ((endMs - startMs) / totalDurationMs) * timelineWidth
                                                );
                                                const width = Math.min(rawWidth, timelineWidth - left);
                                                const progressWidth = Math.max(
                                                    0,
                                                    Math.min(width, width * (Number(project.status) / 100))
                                                );
                                                const isHovered = hoveredProject === project.id;

                                                return (
                                                    <div
                                                        key={project.id || `${project.project_name}-${index}`}
                                                        className="custom_gantt_row"
                                                        style={{ height: `${timelineConfig?.rowHeight || 48}px` }}
                                                    >
                                                        <div
                                                            className={`custom_gantt_bar ${isHovered ? 'hovered' : ''}`}
                                                            style={{
                                                                left: `${left}px`,
                                                                width: `${width}px`,
                                                                backgroundColor: barBackground,
                                                                borderColor: barColor,
                                                            }}
                                                            onMouseEnter={() => setHoveredProject(project.id)}
                                                            onMouseMove={(e) => {
                                                                setMousePosition({ x: e.clientX, y: e.clientY });
                                                            }}
                                                            onMouseLeave={() => setHoveredProject(null)}
                                                        >
                                                            <div
                                                                className="custom_gantt_progress"
                                                                style={{
                                                                    width: `${progressWidth}px`,
                                                                    backgroundColor: barColor,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                </div>
                                {/* Tooltip rendered with fixed positioning */}
                                {hoveredProject && (() => {
                                    const project = sortedProjects.find(p => p.id === hoveredProject);
                                    if (!project) return null;
                                    const contractor = isSubContractor 
                                        ? project.user?.company_name 
                                        : project.assign_to?.company_name;
                                    
                                    const tooltipHeight = 180;
                                    const tooltipWidth = 260;
                                    const viewportHeight = window.innerHeight;
                                    const viewportWidth = window.innerWidth;
                                    const showAbove = mousePosition.y + tooltipHeight + 20 > viewportHeight;
                                    const showLeft = mousePosition.x + tooltipWidth + 20 > viewportWidth;
                                    
                                    return (
                                        <div 
                                            className={`custom_gantt_tooltip_fixed ${showAbove ? 'above' : 'below'}`}
                                            style={{
                                                left: showLeft 
                                                    ? `${mousePosition.x - tooltipWidth - 10}px`
                                                    : `${mousePosition.x + 15}px`,
                                                top: showAbove 
                                                    ? `${mousePosition.y - tooltipHeight - 10}px`
                                                    : `${mousePosition.y + 15}px`,
                                            }}
                                        >
                                            <div className="tooltip_header">{project.project_name}</div>
                                            <div className="tooltip_row">
                                                <span className="tooltip_label">{isSubContractor ? 'General Contractor' : 'Sub-Contractor'}:</span>
                                                <span className="tooltip_value">{contractor || 'N/A'}</span>
                                            </div>
                                            <div className="tooltip_row">
                                                <span className="tooltip_label">Start:</span>
                                                <span className="tooltip_value">{formatDate(project.project_start_date)}</span>
                                            </div>
                                            <div className="tooltip_row">
                                                <span className="tooltip_label">End:</span>
                                                <span className="tooltip_value">{formatDate(project.project_end_date)}</span>
                                            </div>
                                            <div className="tooltip_row">
                                                <span className="tooltip_label">Duration:</span>
                                                <span className="tooltip_value">{calculateDuration(project.project_start_date, project.project_end_date)}</span>
                                            </div>
                                            <div className="tooltip_row">
                                                <span className="tooltip_label">Progress:</span>
                                                <span className="tooltip_value">{Number(project.status).toFixed(0)}%</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                </div>

                {/* Info Section */}
                <div className="schedule_info">
                    <InfoOutlinedIcon fontSize="small" />
                    <p>Each bar shows the full project timeline. The darker filled portion indicates how much work is complete. Hover over any bar for details.</p>
                </div>
            </div>
        </div>
    );
}
